import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import {
  BehaviorSubject,
  combineLatest,
  mergeMap,
  Observable,
  of,
  Subscription,
  distinctUntilChanged,
  filter,
  map,
  take,
} from 'rxjs';
import { Store } from '@ngrx/store';

import { AppState } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import {
  FinnairAirCalendarChangeListResponse,
  FinnairBoundItem,
  FinnairCalendarChangeRequest,
  FinnairOrder,
} from '@fcom/dapi/api/models';
import {
  ButtonLayout,
  ButtonMode,
  ButtonPosition,
  ButtonSize,
  ButtonTheme,
  IconPosition,
  LoaderTheme,
  ModalButtons,
  NotificationLayout,
  NotificationTheme,
  DateRange,
} from '@fcom/ui-components';
import { snapshot } from '@fcom/rx';
import { LocalDate, TzDate, unsubscribe } from '@fcom/core/utils';
import { GtmService } from '@fcom/common/gtm';
import { ElementActions, ElementTypes, GaContext, MediaQueryService } from '@fcom/common';
import { navigateToError } from '@fcom/voluntary-change/utils/error-routing.utils';
import { VoluntaryChangeService } from '@fcom/voluntary-change/services/voluntary-change.service';
import { VOLUNTARY_CHANGE_STEPS } from '@fcom/voluntary-change/voluntary-change-config';
import { orderData, getChangeableBoundIds } from '@fcom/common-booking';
import { UtilsService } from '@fcom/common-booking/services/utils.service';

import { OffersService } from '../../../../services';
import { getChangeBounds, getChangeDates } from '../../../../store/selectors/change.selector';
import { ChangeActions } from '../../../../store/actions/change.actions';

const DATE_MATRIX_MAX_DAYS_PER_BOUND = 7;

@Component({
  selector: 'fin-mmb-select-date',
  templateUrl: './select-date.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingSelectDateComponent implements OnInit, OnDestroy {
  readonly NotificationTheme = NotificationTheme;

  @Input() booking$: Observable<FinnairOrder>;
  @Input() changeableBoundIds$: Observable<string[]>;
  @Output() modalClosed = new EventEmitter<boolean>();

  readonly ButtonMode = ButtonMode;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly IconPosition = IconPosition;
  readonly NotificationLayout = NotificationLayout;
  readonly ModalButtons: typeof ModalButtons = ModalButtons;
  readonly LoaderTheme = LoaderTheme;
  readonly ButtonPosition = ButtonPosition;
  readonly ButtonLayout = ButtonLayout;

  private currentDate: LocalDate = LocalDate.now();
  datepickerErrorStatuses = [400, 404];
  selectedBoundsForChange$: Observable<FinnairBoundItem[]>;

  selectableDatesRange: DateRange = [this.currentDate, this.currentDate.plusDays(361)];
  disabledDateRanges$?: Observable<DateRange[]>;
  initialSelectedDates$: Observable<[LocalDate] | DateRange>;
  selectedBoundsWithDates$: Observable<Record<string, string>>;
  loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  canContinue$: Observable<boolean>;
  isDateRange$: Observable<boolean>;
  subscriptions: Subscription = new Subscription();
  dates: [string, string] | [string];
  isMobile$: Observable<boolean>;
  showError$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private store$: Store<AppState>,
    private utilsService: UtilsService,
    private offersService: OffersService,
    private router: Router,
    private lang: LanguageService,
    private gtmService: GtmService,
    private voluntaryChangeService: VoluntaryChangeService,
    private mediaQueryService: MediaQueryService
  ) {}

  ngOnInit(): void {
    this.isMobile$ = this.mediaQueryService.isBreakpoint$('mobile');
    this.booking$ = this.store$.pipe(orderData());
    this.changeableBoundIds$ = this.store$.pipe(getChangeableBoundIds());

    this.selectedBoundsForChange$ = this.store$.pipe(getChangeBounds());

    this.isDateRange$ = this.selectedBoundsForChange$.pipe(map((bounds) => bounds.filter(Boolean).length === 2));

    this.initialSelectedDates$ = this.store$.pipe(
      getChangeDates(),
      distinctUntilChanged(),
      map((dates) => dates ?? []),
      map((dates) => dates.filter(Boolean).map((date) => new TzDate(date).toLocalDate())),
      map((dates) => dates as [LocalDate] | DateRange)
    );

    this.disabledDateRanges$ = combineLatest([this.selectedBoundsForChange$, this.booking$]).pipe(
      map(([selectedBoundsForChange, booking]) => [
        this.utilsService.getDisabledDateRange(selectedBoundsForChange, booking),
      ])
    );

    const selectedDates$ = this.store$.pipe(getChangeDates(), distinctUntilChanged());
    const selectedBounds$: Observable<FinnairBoundItem[]> = this.store$.pipe(getChangeBounds());

    this.selectedBoundsWithDates$ = combineLatest([selectedBounds$, selectedDates$]).pipe(
      filter(([selectedBounds, selectedDates]) => selectedBounds !== null && selectedDates !== null),
      map(([selectedBounds, selectedDates]) =>
        selectedBounds.filter(Boolean).reduce((boundsWithDates, bound, index) => {
          const dateIndex = selectedDates[index] ? index : 0;
          boundsWithDates[bound.id] = selectedDates[dateIndex];
          return boundsWithDates;
        }, {})
      )
    );

    this.canContinue$ = combineLatest([this.selectedBoundsForChange$, selectedDates$]).pipe(
      map(([bounds, selectedDates]) => bounds.filter(Boolean).length === selectedDates.filter(Boolean).length)
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  onSelectedDatesChange(dates: [string, string] | [string]): void {
    if (this.dates == null || !this.dates.every((date, index) => date === dates[index])) {
      this.showError$.next(false);
      this.dates = dates;
      this.store$.dispatch(ChangeActions.setDates({ dates }));
    }
  }

  closeModal(): void {
    this.subscriptions.add(
      combineLatest([this.booking$, this.changeableBoundIds$])
        .pipe(take(1))
        .subscribe(([booking, changeableBoundIds]) => {
          this.modalClosed.next(true);
          this.loading$.next(false);
          this.gtmService.trackElement(
            'view-pnr_change_abort-selectdate',
            GaContext.MANAGE_BOOKING,
            ElementTypes.MODAL,
            undefined,
            ElementActions.CLOSE,
            `recLoc-${booking?.otherInformation.analyticsToken}`
          );

          if (changeableBoundIds.length === 1) {
            this.voluntaryChangeService.goToViewPNR();
          } else {
            this.voluntaryChangeService.setStep({ step: VOLUNTARY_CHANGE_STEPS.SELECT_BOUNDS });
          }
        })
    );
  }

  loadCalendarMatrix(): void {
    this.loading$.next(true);
    this.subscriptions.add(
      combineLatest([this.booking$, this.selectedBoundsWithDates$])
        .pipe(
          take(1),
          map(([booking, selectedBoundsWithDates]) => {
            return {
              booking,
              searchParams: this.calendarSearchParams(booking, selectedBoundsWithDates),
            };
          }),
          mergeMap(({ booking, searchParams }) =>
            combineLatest([
              of(booking),
              this.offersService.findAirCalendarChange(booking.id, booking.hash, searchParams),
            ])
          )
        )
        .subscribe({
          next: ([booking, list]) => {
            this.gtmService.trackElement(
              this.calendarTrackingEventName(list),
              GaContext.MANAGE_BOOKING,
              ElementTypes.BUTTON,
              undefined,
              ElementActions.CLICK,
              `recLoc-${booking?.otherInformation.analyticsToken}`
            );
            this.store$.dispatch(ChangeActions.setChangeList({ list }));

            this.voluntaryChangeService.setStep({ step: VOLUNTARY_CHANGE_STEPS.SELECT_MATRIX });
            this.loading$.next(false);
          },
          error: (error: unknown) => {
            this.loading$.next(false);
            if (this.datepickerErrorStatuses.includes((error as HttpErrorResponse)?.status)) {
              this.showError$.next(true);
            } else {
              navigateToError(this.router, this.lang.langValue, 'VOLUNTARY_CHANGE_ERROR_GETTING_OFFERS');
            }
          },
        })
    );
  }

  private dateOptions(numberOfDates: number, changedBoundsCount: number) {
    if (numberOfDates === 0) {
      return 'none';
    }
    return this.allDatesInMatrixHaveFlights(numberOfDates, changedBoundsCount) ? 'all' : 'some';
  }

  private allDatesInMatrixHaveFlights(numberOfDates: number, changedBoundsCount: number): boolean {
    return numberOfDates === DATE_MATRIX_MAX_DAYS_PER_BOUND ** changedBoundsCount;
  }

  private calendarTrackingEventName(calendarChangeList: FinnairAirCalendarChangeListResponse): string {
    const changedBoundsCount = calendarChangeList.searchParameters.itinerary.filter(({ toChange }) => toChange).length;
    const numberOfDates = calendarChangeList.airCalendars.length;
    return `view-pnr_change_dates_all-flights_change-${
      changedBoundsCount === 1 ? 'one' : 'both'
    }_date-options-${this.dateOptions(numberOfDates, changedBoundsCount)}`;
  }

  private calendarSearchParams(
    booking: FinnairOrder,
    selectedBoundsWithDates: Record<string, string>
  ): FinnairCalendarChangeRequest {
    return {
      directFlights: false, // explicitly set to false to decrease 'no offers found'-cases
      itinerary: booking.bounds.map(({ id, departure: { dateTime } }) => {
        return {
          toChange: Boolean(selectedBoundsWithDates[id]),
          boundId: id,
          departureDate: selectedBoundsWithDates[id] || snapshot(this.utilsService.getDate(dateTime, 'yyyy-MM-dd')),
        };
      }),
    };
  }
}
